import { useEffect, useState } from 'react';
import { BOX_TYPES, makeLabel, type Make } from '../lib/boxTypes.ts';
import { api, fmt, fmtDate, type Named } from '../api.ts';
import BoxCombo from './BoxCombo.tsx';
import OptionInput from './OptionInput.tsx';
import SearchSelect from './SearchSelect.tsx';

/** A saved box master for this customer, with what's on file for it. */
interface BoxHistory {
  preset_id: number;
  box_name: string;
  specification: string | null;
  box_size: string | null;
  box_type: string | null;
  make: Make | null;
  ups: number | null;
  unit_price: string | null;
  /** Null for a box pre-registered ahead of its first order. */
  last_ordered: string | null;
  last_quantity: string | null;
  code: string | null;
  /** The board the box is made on, from Board stock. */
  board_name: string | null;
  boards: {
    boardSpecification: string | null; size: string | null;
    gsm: string | null; supplierId: number | null; purchasePrice: string | null;
  }[] | null;
}

/** One field that disagreed with the saved master when the order was placed. */
interface DriftField { field: string; from: unknown; to: unknown; }
interface Drift { presetId: number; boxName: string; fields: DriftField[]; }

const FIELD_LABEL: Record<string, string> = {
  boxType: 'Box type', make: 'Make', boxSize: 'Box size', ups: 'Ups', unitPrice: 'Rate',
  boardSpecification: 'Board', sheetSize: 'Sheet size', gsm: 'GSM',
  supplierId: 'Supplier', purchasePrice: 'Purchase price',
};

/**
 * One side of a drift comes from the database and the other from this form,
 * and for a NUMERIC column those are not the same shape: node-postgres hands
 * back `"150.000"` while the value just typed is the number `180`. Printed
 * raw, the panel read "150.00 -> 180" and looked like a formatting bug in the
 * data. Numbers are normalised through Number(); anything that is not a
 * number (a board spec, a box size like "46 X 61 CM") is left alone.
 */
function driftValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (v === 'MONO' || v === 'MASTER' || v === 'KRAFT_PRINT' || v === 'BUFFER') return makeLabel(v);
  const n = Number(v);
  return typeof v !== 'boolean' && String(v).trim() !== '' && Number.isFinite(n)
    ? String(n)
    : String(v);
}

interface ItemDraft {
  productName: string;
  specification: string;
  boxSize: string;
  boxType: string;
  make: Make | null;
  ups: string;
  deliveryDate: string;
  quantity: string;
  unitPrice: string;
  /** Which past box this line was filled from, so it fills once, not per key. */
  filledFrom: string;
  /** The saved master this line matched, if any -- sent back so the order
   *  records exactly which box it came from. */
  presetId: number | null;
}

const emptyItem = (): ItemDraft => ({
  productName: '', specification: '', boxSize: '', boxType: '', make: null, ups: '',
  deliveryDate: '', quantity: '', unitPrice: '',
  filledFrom: '', presetId: null,
});

const today = () => new Date().toISOString().slice(0, 10);

/**
 * A line filled from a saved box: size, type, ups and rate come back with it.
 * The values are COPIED, so an order placed today is never rewritten by what
 * happens to a later one -- and the quantity is deliberately left as it is,
 * because what they wanted last time is the one thing that is usually
 * different. (The board comes back on the job card, from the box.)
 */
function fillFrom(it: ItemDraft, match: BoxHistory): ItemDraft {
  return {
    ...it,
    filledFrom: match.box_name,
    presetId: match.preset_id,
    specification: match.specification ?? '',
    boxSize: match.box_size ?? '',
    boxType: match.box_type ?? '',
    make: match.make ?? null,
    ups: match.ups ? String(match.ups) : '',
    unitPrice: match.unit_price ? String(Number(match.unit_price)) : '',
  };
}

export default function NewOrderForm({
  onCancel, onCreated,
}: { onCancel: () => void; onCreated: (msg: string) => void }) {
  const [customers, setCustomers] = useState<(Named & { code: string | null })[]>([]);
  const [history, setHistory] = useState<BoxHistory[]>([]);
  const [customerId, setCustomerId] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [poDate, setPoDate] = useState(today());
  const [priority, setPriority] = useState('NORMAL');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<ItemDraft[]>([emptyItem()]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  /** Set once the order is saved; non-null only when something disagreed with
   *  a saved box master. Shown instead of the form until resolved. */
  const [drifts, setDrifts] = useState<Drift[] | null>(null);
  /**
   * Resolved boxes stay on screen showing what was decided, rather than
   * vanishing. With three boxes to settle, removing each one as it is handled
   * leaves no sense of progress and makes Done light up for no visible reason.
   */
  const [resolved, setResolved] = useState<Record<number, 'updated' | 'oneoff'>>({});
  const [driftError, setDriftError] = useState('');
  const [createdMsg, setCreatedMsg] = useState('');

  useEffect(() => {
    // Every active company, not one page of the customer list.
    api.get<{ customers: (Named & { code: string | null })[] }>('/api/reference/customers')
      .then((c) => setCustomers(c.customers))
      .catch(() => setError('Could not load companies'));
  }, []);

  // What this company has ordered before, so a repeat fills itself in.
  useEffect(() => {
    // A box belongs to one company: lines filled from the last company's boxes
    // are no longer repeats of anything here. What was typed stays.
    setItems((prev) => prev.map((it) => (it.filledFrom || it.presetId
      ? { ...it, filledFrom: '', presetId: null } : it)));
    if (!customerId) { setHistory([]); return; }
    api.get<{ boxes: BoxHistory[] }>(`/api/customers/${customerId}/box-history`)
      .then((r) => setHistory(r.boxes))
      .catch(() => setHistory([]));
  }, [customerId]);

  function patchItem(i: number, patch: Partial<ItemDraft>) {
    setItems((prev) => prev.map((it, n) => (n === i ? { ...it, ...patch } : it)));
  }

  /**
   * Typing: the line's box is whatever is typed. A name that matches a saved
   * box exactly fills the line from it, the same as picking it from the list;
   * changing it away from that name is a different box, so the "repeat" goes.
   */
  function nameChanged(i: number, name: string) {
    const match = history.find(
      (h) => h.box_name.trim().toLowerCase() === name.trim().toLowerCase());
    setItems((prev) => prev.map((it, n) => {
      if (n !== i) return it;
      if (!match) return { ...it, productName: name, presetId: null, filledFrom: '' };
      if (it.filledFrom === match.box_name) return { ...it, productName: name };
      return fillFrom({ ...it, productName: name }, match);
    }));
  }

  /** A saved box chosen from the list: its name, and the whole line from it. */
  function pickBox(i: number, box: BoxHistory) {
    setItems((prev) => prev.map((it, n) =>
      (n === i ? fillFrom({ ...it, productName: box.box_name }, box) : it)));
  }

  /** Start the line's box again: the figures filled from a saved box go with it. */
  function changeBox(i: number) {
    setItems((prev) => prev.map((it, n) => (n === i ? {
      ...it, productName: '', filledFrom: '', presetId: null,
      specification: '', boxSize: '', boxType: '', make: null, ups: '', unitPrice: '',
    } : it)));
    setTimeout(() => document.getElementById(`box-${i}`)?.focus(), 0);
  }

  const problem = (() => {
    if (!customerId) return 'Choose a company';
    if (items.length === 0) return 'Add at least one line';
    for (const [n, it] of items.entries()) {
      if (!it.productName.trim()) return `Line ${n + 1}: enter the box name`;
      if (!it.quantity || Number(it.quantity) <= 0) return `Line ${n + 1}: enter a quantity`;
    }
    return '';
  })();

  const total = items.reduce(
    (sum, it) => sum + Number(it.quantity || 0) * Number(it.unitPrice || 0),
    0,
  );

  async function submit() {
    if (problem) return;
    setBusy(true);
    setError('');
    try {
      const res = await api.post<{
        purchaseOrder: {
          id: number; salesOrder: string; poNumber: string | null; jobNumbers: string[]; drifts: Drift[];
        };
      }>(
        '/api/purchase-orders',
        {
          ...(poNumber.trim() ? { poNumber: poNumber.trim() } : {}),
          customerId: Number(customerId),
          poDate,
          priority,
          ...(notes.trim() ? { notes: notes.trim() } : {}),
          items: items.map((it) => {
            return {
              ...(it.presetId ? { presetId: it.presetId } : {}),
              productName: it.productName.trim(),
              ...(it.specification.trim() ? { specification: it.specification.trim() } : {}),
              ...(it.boxSize.trim() ? { boxSize: it.boxSize.trim() } : {}),
              ...(it.boxType.trim() ? { boxType: it.boxType.trim() } : {}),
              ...(it.make ? { make: it.make } : {}),
              ...(it.ups ? { ups: Number(it.ups) } : {}),
              ...(it.deliveryDate ? { deliveryDate: it.deliveryDate } : {}),
              quantity: Number(it.quantity),
              ...(it.unitPrice ? { unitPrice: Number(it.unitPrice) } : {}),
              boardRequired: true,
            };
          }),
        },
      );
      const msg = `${res.purchaseOrder.salesOrder} created · ${res.purchaseOrder.jobNumbers.join(', ')}`;
      if (res.purchaseOrder.drifts.length > 0) {
        // Shown instead of calling onCreated immediately -- the order is
        // already saved either way, this is purely about whether to also
        // update the saved template for next time.
        setDrifts(res.purchaseOrder.drifts);
        setCreatedMsg(msg);
      } else {
        onCreated(msg);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not create the order';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  async function updateTemplate(d: Drift) {
    setDriftError('');
    try {
      await api.patch(`/api/customers/${customerId}/presets/${d.presetId}`,
        Object.fromEntries(d.fields.map((f) => [f.field, f.to])));
      setResolved((r) => ({ ...r, [d.presetId]: 'updated' }));
    } catch (err) {
      // Previously the row was dropped regardless, so a failed template
      // update looked exactly like a successful one.
      setDriftError(err instanceof Error
        ? `Could not update ${d.boxName}: ${err.message}`
        : `Could not update ${d.boxName}`);
    }
  }

  if (drifts) {
    const pending = drifts.filter((d) => !resolved[d.presetId]);
    return (
      <div className="panel" style={{ maxWidth: 680 }}>
        <div className="panel-head">
          <h2>
            Order saved{pending.length > 0 && ' — '}
            {pending.length === 1 && 'one box to check'}
            {pending.length > 1 && `${pending.length} boxes to check`}
          </h2>
        </div>
        <div style={{ padding: 16 }}>
          <div className="tag ready notice" style={{ marginBottom: 14 }}>
            {createdMsg}
          </div>

          {pending.length > 0 && (
            <div className="hint" style={{ marginBottom: 12 }}>
              These differ from the saved template. Updating changes it for
              next time; a one-off leaves the template alone. Either way this
              order keeps exactly what you entered.
            </div>
          )}

          {driftError && (
            <div className="tag late notice" style={{ marginBottom: 12 }}>{driftError}</div>
          )}

          {drifts.map((d) => {
            const state = resolved[d.presetId];
            return (
              <div key={d.presetId} className="subpanel"
                   style={{ marginBottom: 10, opacity: state ? 0.6 : 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                              alignItems: 'baseline', gap: 10 }}>
                  <strong>{d.boxName}</strong>
                  {state && (
                    <span className="dim" style={{ fontSize: '12px' }}>
                      {state === 'updated' ? 'Template updated' : 'Kept as one-off'}
                    </span>
                  )}
                </div>

                {/* width:auto overrides the global `table { width: 100% }`,
                    which stretched this two-column table across the panel and
                    left the value stranded far from its label. */}
                <table style={{ width: 'auto', marginTop: 6,
                                marginBottom: state ? 0 : 10 }}>
                  <tbody>
                    {d.fields.map((f) => (
                      <tr key={f.field}>
                        <td className="dim" style={{ paddingRight: 16 }}>
                          {FIELD_LABEL[f.field] ?? f.field}
                        </td>
                        <td>
                          <span className="dim">{driftValue(f.from)}</span>
                          <span className="dim" style={{ margin: '0 8px' }}>→</span>
                          <strong>{driftValue(f.to)}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {!state && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn primary" onClick={() => updateTemplate(d)}>
                      Update template
                    </button>
                    <button className="btn"
                            onClick={() => setResolved((r) =>
                              ({ ...r, [d.presetId]: 'oneoff' }))}>
                      Keep as one-off
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          <button className="btn primary" disabled={pending.length > 0}
                  style={{ marginTop: 4 }}
                  onClick={() => onCreated(createdMsg)}>
            {pending.length > 0 ? `${pending.length} still to check` : 'Done'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <h2>New sales order</h2>
        <div className="spacer" />
        <button className="btn" disabled={busy} onClick={onCancel}>Cancel</button>
        <button className="btn primary" disabled={busy || !!problem} onClick={submit}
                title={problem || undefined}>
          {busy ? 'Saving…' : 'Create order'}
        </button>
      </div>

      {error && <div className="error" style={{ margin: 16 }}>{error}</div>}
      {problem && <div className="hint" style={{ margin: '12px 16px 0' }}>{problem}</div>}

      <div style={{ padding: 16 }}>
        <div className="row2" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
          <div className="field">
            <label htmlFor="co" className="req">Company</label>
            <SearchSelect id="co" value={customerId} autoFocus
                          placeholder="Type to search"
                          onChange={setCustomerId}
                          options={customers.map((c) => ({
                            value: String(c.id), label: c.name, ...(c.code ? { hint: c.code } : {}),
                          }))} />
            <div className="hint">New company? Add it in Customers first.</div>
          </div>
          <div className="field">
            <label htmlFor="pon">Customer PO</label>
            <input id="pon" type="text" value={poNumber} placeholder="Leave blank if none"
                   onChange={(e) => setPoNumber(e.target.value)} />
            <div className="hint">The customer's own number, if they gave one</div>
          </div>
          <div className="field">
            <label htmlFor="pod">Order date</label>
            <input id="pod" type="date" value={poDate}
                   onChange={(e) => setPoDate(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="pr">Priority</label>
            <select id="pr" value={priority} onChange={(e) => setPriority(e.target.value)}>
              <option value="LOW">Low</option>
              <option value="NORMAL">Normal</option>
              <option value="HIGH">High</option>
              <option value="URGENT">Urgent</option>
            </select>
          </div>
        </div>
        <div className="field">
          <label htmlFor="nt">Notes</label>
          <input id="nt" type="text" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>

        <h3 style={{ marginTop: 6 }}>Lines</h3>

        {items.map((it, i) => {
          return (
            <div key={i} className="subpanel" style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <strong>Line {i + 1}</strong>
                {it.filledFrom && (() => {
                  const matched = history.find((h) => h.box_name === it.filledFrom);
                  return (
                    <>
                      <span className="tag ready" style={{ marginLeft: 8 }}>
                        {matched?.last_ordered
                          ? `Repeat · filled from ${fmtDate(String(matched.last_ordered).slice(0, 10))}`
                          : 'Filled from a saved box — not ordered yet'}
                      </span>
                      <button className="linkish" style={{ marginLeft: 10 }} onClick={() => changeBox(i)}>
                        Change box
                      </button>
                    </>
                  );
                })()}
                <div style={{ marginLeft: 'auto' }}>
                  {items.length > 1 && (
                    <button className="btn"
                            onClick={() => setItems((p) => p.filter((_, n) => n !== i))}>
                      Remove line
                    </button>
                  )}
                </div>
              </div>

              <div className="row2 line-grid">
                <div className="field span2">
                  <label htmlFor={`box-${i}`} className="req">Box name</label>
                  <BoxCombo id={`box-${i}`} value={it.productName} boxes={history}
                            disabled={!customerId}
                            placeholder={customerId ? 'Find or type a box' : 'Choose a company first'}
                            onType={(v) => nameChanged(i, v)}
                            onPick={(b) => pickBox(i, b as BoxHistory)} />
                </div>
                <div className="field">
                  <label htmlFor={`make-${i}`}>Make</label>
                  <select id={`make-${i}`} value={it.make ?? ''}
                          onChange={(e) => patchItem(i, { make: (e.target.value || null) as Make | null })}>
                    {BOX_TYPES.map((t) => <option key={t.key} value={t.make ?? ''}>{t.label}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label>Delivery date</label>
                  <input type="date" value={it.deliveryDate}
                         onChange={(e) => patchItem(i, { deliveryDate: e.target.value })} />
                </div>

                <div className="field">
                  <label>Box type</label>
                  <OptionInput list="box_type" value={it.boxType}
                               placeholder="E Flute"
                               onChange={(v) => patchItem(i, { boxType: v })} />
                </div>
                <div className="field">
                  <label>Box size</label>
                  <OptionInput list="box_size" value={it.boxSize}
                               placeholder="L x W x H"
                               onChange={(v) => patchItem(i, { boxSize: v })} />
                </div>
                <div className="field">
                  <label title="Boxes per sheet">Ups</label>
                  <input type="number" min="1" value={it.ups} placeholder="Boxes per sheet"
                         onChange={(e) => patchItem(i, { ups: e.target.value })} />
                </div>
                <div className="field">
                  <label className="req">Quantity</label>
                  <input type="number" min="1" value={it.quantity}
                         onChange={(e) => patchItem(i, { quantity: e.target.value })} />
                </div>

                <div className="field">
                  <label>Rate each (pre-GST)</label>
                  <input type="number" min="0" step="0.01" value={it.unitPrice}
                         onChange={(e) => patchItem(i, { unitPrice: e.target.value })} />
                </div>
                <div className="field span3">
                  <label>Specification</label>
                  <input type="text" value={it.specification}
                         onChange={(e) => patchItem(i, { specification: e.target.value })} />
                </div>
              </div>

            </div>
          );
        })}

        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button className="btn" onClick={() => setItems((p) => [...p, emptyItem()])}>
            Add another line
          </button>
          <div style={{ marginLeft: 'auto', alignSelf: 'center' }}>
            <span className="dim">Order value (pre-GST) </span>
            <strong>₹{fmt(total)}</strong>
          </div>
        </div>
      </div>
    </div>
  );
}
